import { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { Calendar, Clock, Sparkles, Star, ArrowRight, Music } from 'lucide-react'
import { getEvents, transformEventForDisplay, type Event } from '../services/eventsService'
import { getEventImageUrl, EVENT_IMAGE_FALLBACK } from '../config/api'

export default function Partys() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [partyGalleryImages, setPartyGalleryImages] = useState<Array<{ src: string; alt: string }>>([])
  const [loadingGallery, setLoadingGallery] = useState(true)

  // Fallback-Bilder (falls API fehlschlägt)
  const allCorporatePartyImages = [
    '2025_03_29HückelsMayParty-15.JPG',
    '2025_03_29HückelsMayParty-18.JPG',
    'HK_1008.JPG',
    'HK_1071.JPG',
    'HK_1091.JPG',
    'HK_1093.JPG',
    'HK_1106.JPG',
    'HK_1120.JPG',
    'HK_92.JPG',
    'HK_929.JPG',
    'HK_956.JPG',
    'HK_968.JPG',
    'LL_1035.JPG',
    'LL_1075.JPG',
    'LL_1157.JPG',
    'LL_1245.JPG',
    'LL_1265.JPG',
    'LL_1271.JPG',
    'LL_1301.JPG',
    'LL_1302.JPG',
    'LL_1388.JPG',
    'LL_1394.JPG',
    'LL_1528.JPG',
    'LL_1535.JPG',
    'LL_1662.JPG',
    'LL_1699.JPG',
    'LL_1806.JPG',
    'LL_1813.JPG',
    'LL_1855.JPG',
    'LL_527.JPG',
    'LL_749.JPG',
    'LL_982.JPG',
  ]

  const fallbackGalleryImages = allCorporatePartyImages.map((fileName) => ({
    src: `/assets/galerie/firmen_events/${fileName}`,
    alt: `Firmen-Event – ${fileName}`,
  }))

  useEffect(() => {
    async function loadEvents() {
      try {
        const loadedEvents = await getEvents()
        setEvents(loadedEvents)
      } catch (error) {
        console.error('Fehler beim Laden der Events:', error)
      } finally {
        setLoading(false)
      }
    }
    loadEvents()
  }, [])

  useEffect(() => {
    async function loadGalleryImages() {
      try {
        const apiBase = import.meta.env.VITE_API_BASE_URL || '/api'
        const response = await fetch(`${apiBase}/list_gallery_images.php?category=firmen_events&skip_dimensions=1`)
        const data = await response.json()

        if (data.success && data.images && data.images.length > 0) {
          // Bilder aus dem Ordner zuerst anzeigen
          const imagesFromFolder = data.images.map((img: any) => ({
            src: img.src || img.file_path,
            alt: img.title || img.filename || `Firmen-Event – ${img.filename}`,
          }))
          setPartyGalleryImages(imagesFromFolder)
        } else {
          // Fallback auf statische Bilder
          setPartyGalleryImages(fallbackGalleryImages)
        }
      } catch (error) {
        console.error('Fehler beim Laden der Galerie-Bilder:', error)
        // Fallback auf statische Bilder
        setPartyGalleryImages(fallbackGalleryImages)
      } finally {
        setLoadingGallery(false)
      }
    }
    loadGalleryImages()
  }, [])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/LL_1035.JPG"
            alt="Partys & Events"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px]" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Music className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Events & Partys</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Events & <span className="italic text-white/70">Partys</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-xs sm:text-sm md:text-base max-w-xl leading-relaxed px-4"
          >
            Unvergessliche Events in historischer Atmosphäre – entdecken Sie unsere kommenden Highlights.
          </motion.p>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          EVENT GRID
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-24 md:py-32">
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-40 left-20 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brand-400" />
              <p className="mt-4 text-white/40">Events werden geladen...</p>
            </div>
          ) : events.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Sparkles className="w-16 h-16 text-white/20 mx-auto mb-6" />
              <h3 className="font-serif text-2xl md:text-3xl text-white font-light mb-4">
                Aktuell keine Events geplant
              </h3>
              <p className="text-white/40 max-w-md mx-auto">
                Schauen Sie bald wieder vorbei für neue spannende Veranstaltungen!
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {events.map((event, index) => {
                const displayEvent = transformEventForDisplay(event)
                const isFeatured = event.featured || index === 0
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="h-full"
                  >
                    <Link to={`/partys/${displayEvent.slug}`} className="group block h-full">
                      <div className="relative h-full flex flex-col bg-gradient-to-br from-gray-900/50 to-gray-900/30 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-brand-500/40 hover:shadow-2xl hover:shadow-brand-500/10 transition-all duration-500">
                        {/* Image Container - vollständig sichtbar */}
                        <div className="relative min-h-64 md:min-h-72 overflow-hidden bg-gray-900 flex items-center justify-center">
                          <motion.img
                            src={getEventImageUrl(displayEvent.imageUrl)}
                            alt={displayEvent.title}
                            className="w-full h-auto max-h-96 object-contain transition-transform duration-700 group-hover:scale-105"
                            onError={(e) => {
                              if ((e.currentTarget as HTMLImageElement).dataset.flyerFallback === '1') return
                              ;(e.currentTarget as HTMLImageElement).dataset.flyerFallback = '1'
                              e.currentTarget.src = EVENT_IMAGE_FALLBACK
                            }}
                          />
                          
                          {/* Gradient Overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent" />
                          
                          {/* Date Badge - Top Left */}
                          <div className="absolute top-4 left-4 z-10">
                            <div className="px-3 py-1.5 bg-red-600 rounded-lg shadow-lg">
                              <div className="flex items-center gap-2 text-white">
                                <Calendar className="w-3.5 h-3.5 text-white" />
                                <span className="text-xs font-bold uppercase tracking-wider text-white">
                                  {displayEvent.date.split('.')[0]}. {displayEvent.date.split('.')[1]}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Featured Badge - Top Right */}
                          {isFeatured && (
                            <motion.div 
                              initial={{ scale: 0.9 }}
                              animate={{ scale: 1 }}
                              className="absolute top-4 right-4 z-10"
                            >
                              <div className="px-3 py-1.5 bg-gradient-to-r from-brand-600 to-brand-700 rounded-full shadow-lg border border-brand-500/30">
                                <div className="flex items-center gap-1.5">
                                  <Star className="w-3.5 h-3.5 text-white fill-white" />
                                  <span className="text-white text-xs font-bold uppercase tracking-wider">
                                    Featured
                                  </span>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </div>

                        {/* Content - Flex-grow für gleichmäßige Höhe */}
                        <div className="flex-1 flex flex-col p-6 bg-gradient-to-b from-gray-900/80 to-gray-900/95">
                          {/* Title */}
                          <h3 className="font-serif text-xl md:text-2xl text-white font-light mb-3 group-hover:text-brand-400 transition-colors duration-300 line-clamp-2">
                            {displayEvent.title}
                          </h3>
                          
                          {/* Date & Time Info */}
                          <div className="flex items-center gap-3 text-xs text-white/60 mb-4">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3.5 h-3.5 text-brand-400" />
                              <span className="font-medium">{displayEvent.date}</span>
                            </div>
                            {displayEvent.time && (
                              <>
                                <span className="text-white/20">•</span>
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3.5 h-3.5 text-brand-400" />
                                  <span className="font-medium">{displayEvent.time}</span>
                                </div>
                              </>
                            )}
                            {displayEvent.location && (
                              <>
                                <span className="text-white/20">•</span>
                                <span className="font-medium truncate max-w-[120px]">{displayEvent.location}</span>
                              </>
                            )}
                          </div>
                          
                          {/* Description */}
                          {displayEvent.description && (
                            <p className="text-white/50 text-sm leading-relaxed line-clamp-2 mb-4 flex-grow">
                              {displayEvent.description}
                            </p>
                          )}
                          
                          {/* Footer with Price and Details */}
                          <div className="flex items-center justify-between pt-4 border-t border-white/10 mt-auto">
                            <span className="text-brand-400 text-sm font-semibold">
                              {displayEvent.price}
                            </span>
                            <div className="flex items-center gap-2 text-white/50 group-hover:text-brand-400 transition-colors">
                              <span className="text-xs font-medium uppercase tracking-wider">Details</span>
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          PARTY IMPRESSIONEN – EIGENER CONTAINER
      ════════════════════════════════════════════════════════════ */}
      <section className="py-16 md:py-24 border-t border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 relative z-10">
          <div className="text-center mb-10 md:mb-14">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-4"
            >
              Private Events
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-serif text-3xl md:text-4xl text-white font-light"
            >
              Party <span className="italic text-white/60">Impressionen</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 text-white/40 max-w-2xl mx-auto text-sm md:text-base"
            >
              Ein paar Eindrücke, wie Ihre Feier bei uns aussehen kann – von stimmungsvollen Privatpartys
              bis zu stilvollen Firmenevents.
            </motion.p>
          </div>

          {loadingGallery ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brand-400" />
              <p className="mt-4 text-white/40">Bilder werden geladen...</p>
            </div>
          ) : partyGalleryImages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <p className="text-white/40">Noch keine Bilder verfügbar.</p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {partyGalleryImages.map((image, index) => (
                <motion.div
                  key={image.src}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative overflow-hidden rounded-xl border border-white/5 bg-white/5"
                >
                  <div className="aspect-[4/3]">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Sparkles className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Private Events</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Planen Sie Ihr<br />
              <span className="italic text-white/60">eigenes Event</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Sie möchten eine private Feier bei uns ausrichten? Kontaktieren Sie uns für ein individuelles Angebot.
            </p>

            <Link to="/kontakt">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3 mx-auto"
              >
                Anfrage stellen
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
